// Persistent cart: share ONE cart across a customer's devices.
//
// The cart itself always lives in Shopify. We only persist a POINTER to it -
// the cart token - against the customer, so a second device resolves to the
// same cart instead of building its own. Shopify then stays the single source
// of truth and there is nothing to diff, merge or reconcile.
//
// Why a token and not the line items: the token does NOT change when the cart
// contents change (quantity 1 -> 2 keeps the same token). It only changes when
// a new cart is created. That keeps metafield writes to roughly one per cart
// lifetime instead of one per click, which is what made the previous
// item-list-syncing implementation unusable.

const CART_ROOT_DOMAIN = '.bouwmaat.nl';
const CART_COOKIE_DAYS = 30;
// Set to the token we adopted, so a bad/stale pointer cannot reload forever.
const CART_SWAP_GUARD_KEY = 'bm_cart_adopted_token';
// Pre-existing sentinel meaning "this customer has no saved cart".
const NO_CART_SENTINEL = 'nullcart';
// Last customer this device was signed in as, so a logout or an account
// switch can be detected on the next load.
const LAST_CUSTOMER_KEY = 'bm_last_customer_id';
// The cart token last known to belong to a signed-in account on this device.
// Lets a logout be detected even when LAST_CUSTOMER_KEY was never written -
// e.g. the shopper only used the account app, where this script does not run.
const ACCOUNT_TOKEN_KEY = 'bm_account_cart_token';

function getPersistentCartCustomerId() {
    return (window.ShopifyTranslations && window.ShopifyTranslations.customerId) || '';
}

function readCartCookieToken() {
    const match = document.cookie.match(/(?:^|;\s*)cart=([^;]*)/);
    if (!match) return '';
    try {
        return decodeURIComponent(match[1]);
    } catch (e) {
        return match[1];
    }
}

function writeCartCookieToken(token) {
    // Shopify sets its own `cart` cookie host-scoped (no Domain attribute).
    // Ours is domain-wide so it can be shared with the account app, but the
    // two are different cookies - and when both are sent the host-scoped one
    // wins. Expire it first, or adopting another device's cart silently does
    // nothing because the stale narrower cookie keeps winning.
    document.cookie = `cart=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=None; Secure=true`;
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + CART_COOKIE_DAYS);
    document.cookie = `cart=${encodeURIComponent(token)}; domain=${CART_ROOT_DOMAIN}; path=/; expires=${expiryDate.toUTCString()}; SameSite=None; Secure=true`;
}

function clearCartCookie() {
    // Both scopes: Shopify's own cookie is host-scoped and a narrower cookie of
    // the same name wins when the browser sends both, so clearing only the
    // domain-wide one leaves the old cart in play.
    document.cookie = `cart=; domain=${CART_ROOT_DOMAIN}; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=None; Secure=true`;
    document.cookie = `cart=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=None; Secure=true`;
}

function readAccountToken() {
    try {
        return localStorage.getItem(ACCOUNT_TOKEN_KEY) || '';
    } catch (e) {
        return '';
    }
}

function writeAccountToken(token) {
    try {
        if (token) localStorage.setItem(ACCOUNT_TOKEN_KEY, token);
        else localStorage.removeItem(ACCOUNT_TOKEN_KEY);
    } catch (e) { /* private mode */ }
}

function readLastCustomerId() {
    try {
        return localStorage.getItem(LAST_CUSTOMER_KEY) || '';
    } catch (e) {
        return '';
    }
}

function writeLastCustomerId(customerId) {
    try {
        localStorage.setItem(LAST_CUSTOMER_KEY, customerId || '');
    } catch (e) { /* private mode */ }
}

/**
 * Move this device off the account's cart and onto a copy of its own.
 *
 * Called when the signed-in customer changes - a logout, or switching
 * accounts. Without it the device keeps the same cart token, so edits made
 * while signed out still land on the previous customer's cart and show up on
 * their other devices.
 *
 * The items are copied so the shopper does not visibly lose their cart; the
 * account keeps the original, which is also what lets a later login restore
 * products that were removed while signed out.
 */
async function detachFromAccountCart(cart, signedOutCustomerId) {
    let previousToken = (cart && cart.token) || '';
    let lines = toCopyLines((cart && cart.items) || []);

    // Shopify's logout can hand the browser a fresh empty cart (or stop
    // serving the account's cart to a signed-out visitor), so by now there
    // may be nothing left to read. Fall back to the copy taken while the
    // customer was still signed in.
    if (!lines.length) {
        const snapshot = readAccountSnapshot();
        if (snapshot && signedOutCustomerId && snapshot.customerId === signedOutCustomerId) {
            lines = snapshot.lines;
            previousToken = snapshot.token || previousToken;
        }
    }
    writeAccountSnapshot('', null);

    // Stash the lines BEFORE touching the cookie. The copy below is
    // best-effort - a 429, a 422 on one line, or another script creating a
    // cart concurrently while there is no cookie can all lose it - so the
    // next load re-checks and re-adds whatever did not make it.
    writePendingCopy(lines, previousToken);
    logCartSync('detach', {lines: lines.length, from: shortToken(previousToken), emptyAtLogout: !(cart && cart.item_count)});
    writeLoginMerge(null);
    if (lines.length) armSafetyNet();
    clearCartCookie();

    if (lines.length) {
        // Only copy into a cart that is demonstrably NOT the one we just left.
        // If clearing the cookie did not take - a second `cart` cookie at
        // another scope, a blocked write - then adding here would add the
        // lines back into the same cart and double every quantity. Re-read
        // first and bail out rather than risk that.
        let detached = false;
        try {
            const check = await cartFetch('/cart.js', {headers: {Accept: 'application/json'}});
            if (check.ok) {
                const current = await check.json();
                detached = current.token !== previousToken;
            }
        } catch (e) {
            detached = false;
        }

        if (detached) {
            await addLines(lines);
        } else {
            console.error(
                'Persistent cart: still on the previous cart after clearing the ' +
                    'cookie - skipping the copy so quantities are not doubled.',
            );
        }
    }

    window.location.reload();
}

// Signed-out copy of the account cart that has not been confirmed on this
// device's cart yet. localStorage, not sessionStorage: logout can bounce
// through the account app and Shopify, and must survive that.
const PENDING_COPY_KEY = 'bm_detach_pending_copy';
const PENDING_COPY_MAX_ATTEMPTS = 3;

function cartFetch(url, options) {
    // Goes through the shared queue so it gets the same 429 retries and is
    // not interleaved with the storefront's own cart writes.
    return window.CartQueue ? window.CartQueue.fetch(url, options) : fetch(url, options);
}

function toCopyLines(items) {
    // Pallet/surcharge lines are recalculated by the storefront for the new
    // cart; copying them can also 422 the whole batch.
    const autoAdded = getAutoAddedVariantIds();
    return items
        .filter((item) => item && !autoAdded.has(String(item.variant_id)))
        .map((item) => ({
            id: item.variant_id,
            quantity: item.quantity,
            properties: item.properties || {},
        }));
}

function readPendingCopy() {
    try {
        const parsed = JSON.parse(localStorage.getItem(PENDING_COPY_KEY) || 'null');
        return parsed && Array.isArray(parsed.lines) ? parsed : null;
    } catch (e) {
        return null;
    }
}

function writePendingCopy(lines, fromToken, attempts) {
    try {
        if (lines && lines.length) {
            localStorage.setItem(
                PENDING_COPY_KEY,
                JSON.stringify({lines, fromToken, attempts: attempts || 0}),
            );
        } else {
            localStorage.removeItem(PENDING_COPY_KEY);
        }
    } catch (e) { /* private mode */ }
}

// The account cart's lines as last seen while signed in. Only read when the
// cart is already empty by the time the logout is detected.
const ACCOUNT_SNAPSHOT_KEY = 'bm_account_cart_snapshot';
// How long a logout click may wait for the snapshot before navigating.
const LOGOUT_SNAPSHOT_TIMEOUT_MS = 1500;

function readAccountSnapshot() {
    try {
        const parsed = JSON.parse(localStorage.getItem(ACCOUNT_SNAPSHOT_KEY) || 'null');
        return parsed && Array.isArray(parsed.lines) && parsed.lines.length ? parsed : null;
    } catch (e) {
        return null;
    }
}

// Pass an empty cart (or null) to drop the snapshot, so products removed or
// bought while signed in can never come back on logout.
function writeAccountSnapshot(customerId, cart) {
    try {
        const lines = toCopyLines((cart && cart.items) || []);
        if (customerId && lines.length) {
            localStorage.setItem(
                ACCOUNT_SNAPSHOT_KEY,
                JSON.stringify({customerId, token: cart.token || '', lines}),
            );
        } else {
            localStorage.removeItem(ACCOUNT_SNAPSHOT_KEY);
        }
    } catch (e) { /* private mode */ }
}

// Take a last snapshot when the shopper clicks logout, while the session can
// still read the account cart. Bounded so logout is never held up.
function watchLogoutLinks(customerId) {
    document.addEventListener('click', (event) => {
        const link = event.target && event.target.closest && event.target.closest('a[href*="/account/logout"]');
        if (!link || event.defaultPrevented || event.button > 0 ||
            event.metaKey || event.ctrlKey || event.shiftKey) return;

        event.preventDefault();
        const snapshot = fetch('/cart.js', {headers: {Accept: 'application/json'}})
            .then((response) => (response.ok ? response.json() : null))
            .then((cart) => { if (cart) writeAccountSnapshot(customerId, cart); });
        const timeout = new Promise((resolve) => setTimeout(resolve, LOGOUT_SNAPSHOT_TIMEOUT_MS));
        Promise.race([snapshot, timeout])
            .catch(() => {})
            .then(() => { window.location.href = link.href; });
    }, true);
}

// Keep the snapshot current as the storefront re-reads the cart.
function watchAccountCart(customerId) {
    document.addEventListener('cart:state:updated', (event) => {
        const cart = event && event.detail && event.detail.cart;
        if (cart && cart.token && cart.token === readAccountToken()) {
            writeAccountSnapshot(customerId, cart);
        }
    });
}

// Returns true when Shopify accepted the lines, false when it is worth
// retrying later (rate limit, network, server error).
async function addLines(lines) {
    const post = (items) =>
        cartFetch('/cart/add.js', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({items}),
        });
    try {
        const response = await post(lines);
        if (response.ok) return true;
        // One unavailable or over-stock line rejects the whole batch. Add the
        // rest one by one so a single bad product does not empty the cart.
        if (response.status === 422 && lines.length > 1) {
            for (const line of lines) {
                const single = await post([line]);
                if (!single.ok && single.status !== 422) return false;
            }
            return true;
        }
        console.error('Persistent cart: copy rejected with HTTP', response.status);
        return response.status === 422;
    } catch (e) {
        console.error('Persistent cart: failed to copy cart on detach', e);
        return false;
    }
}

/**
 * Finish a detach copy that did not land. Runs on the signed-out load after
 * detachFromAccountCart; only re-adds variants missing from the current
 * cart, so a copy that did succeed is never doubled. Returns true when it
 * changed the cart and the page is reloading.
 */
async function completePendingCopy(cart) {
    const pending = readPendingCopy();
    if (!pending) return false;

    const attempts = (pending.attempts || 0) + 1;
    const onSameCart = cart && cart.token && cart.token === pending.fromToken;
    if (onSameCart || attempts > PENDING_COPY_MAX_ATTEMPTS) {
        // Still on the account cart (nothing was lost), or we have tried
        // enough - stop rather than loop reloads.
        writePendingCopy(null);
        return false;
    }

    const present = new Set(((cart && cart.items) || []).map((item) => String(item.variant_id)));
    const missing = pending.lines.filter((line) => !present.has(String(line.id)));
    if (!missing.length) {
        writePendingCopy(null);
        return false;
    }

    writePendingCopy(pending.lines, pending.fromToken, attempts);
    await addLines(missing);
    window.location.reload();
    return true;
}


// ---------------------------------------------------------------------------
// Extra checks. Everything below only acts when the normal flow above did not
// land; on a normal login or logout each of them is a no-op.
// ---------------------------------------------------------------------------

// Ring buffer of cart-sync decisions on this device, for QA to copy with
// copy(localStorage.bm_cart_sync_log) when a cart goes missing. Tokens are
// shortened; nothing leaves the browser.
const SYNC_LOG_KEY = 'bm_cart_sync_log';
const SYNC_LOG_MAX = 60;

function shortToken(token) {
    return token ? String(token).slice(0, 8) : '';
}

function logCartSync(event, data) {
    try {
        console.info('[CartSync]', event, data || '');
    } catch (e) { /* no console */ }
    try {
        const log = JSON.parse(localStorage.getItem(SYNC_LOG_KEY) || '[]');
        log.push(Object.assign({t: new Date().toISOString(), event, path: window.location.pathname}, data || {}));
        localStorage.setItem(SYNC_LOG_KEY, JSON.stringify(log.slice(-SYNC_LOG_MAX)));
    } catch (e) { /* private mode, full storage or no console */ }
}

// Logout safety net. After a logout the shopper's products live on a guest
// cart. If, within SAFETY_NET_MS, that guest cart is suddenly replaced by an
// EMPTY cart with a different token (a cookie swapped underneath them), point
// the browser back at the previous guest cart - but only after Shopify
// confirms it still exists with products. Nothing is re-added, so nothing can
// be duplicated, and a cart that is gone (order placed, expired) is left gone.
// Emptying the cart yourself keeps the same token and disarms the net.
const SAFETY_NET_KEY = 'bm_logout_safety_net';
const SAFETY_NET_MS = 30 * 60 * 1000;
const SAFETY_NET_MAX_RESTORES = 2;

function readSafetyNet() {
    try {
        const parsed = JSON.parse(localStorage.getItem(SAFETY_NET_KEY) || 'null');
        return parsed && typeof parsed.armedAt === 'number' ? parsed : null;
    } catch (e) {
        return null;
    }
}

function writeSafetyNet(net) {
    try {
        if (net) localStorage.setItem(SAFETY_NET_KEY, JSON.stringify(net));
        else localStorage.removeItem(SAFETY_NET_KEY);
    } catch (e) { /* private mode */ }
}

function armSafetyNet() {
    writeSafetyNet({armedAt: Date.now(), guestToken: '', restores: 0});
}

function disarmSafetyNet(reason) {
    if (!readSafetyNet()) return;
    writeSafetyNet(null);
    logCartSync('net-disarm', {reason});
}

// Guest loads only. Returns true when it reconnected and the page is reloading.
async function checkSafetyNet(cart, isEmpty) {
    const net = readSafetyNet();
    if (!net) return false;
    if (Date.now() - net.armedAt > SAFETY_NET_MS) {
        disarmSafetyNet('expired');
        return false;
    }

    // Remember the guest cart that holds the shopper's products.
    if (!isEmpty) {
        if (cart.token && cart.token !== net.guestToken && cart.token !== readAccountToken()) {
            writeSafetyNet({...net, guestToken: cart.token});
        }
        return false;
    }

    if (!net.guestToken) return false; // no guest cart with products seen yet
    if (cart && cart.token === net.guestToken) {
        disarmSafetyNet('emptied-by-shopper');
        return false;
    }
    if (net.restores >= SAFETY_NET_MAX_RESTORES) {
        disarmSafetyNet('max-restores');
        return false;
    }
    writeSafetyNet({...net, restores: net.restores + 1});

    // Ask Shopify whether the previous guest cart is still there.
    writeCartCookieToken(net.guestToken);
    let previous = null;
    try {
        const response = await cartFetch('/cart.js', {headers: {Accept: 'application/json'}});
        if (response.ok) previous = await response.json();
    } catch (e) {
        previous = null;
    }

    if (previous && previous.token === net.guestToken && previous.item_count > 0) {
        logCartSync('net-reconnect', {to: shortToken(net.guestToken), from: shortToken(cart && cart.token), items: previous.item_count});
        window.location.reload();
        return true;
    }

    // Gone (order placed, expired) or unreachable: go back to the cart we had.
    if (cart && cart.token) writeCartCookieToken(cart.token);
    else clearCartCookie();
    disarmSafetyNet('previous-cart-gone');
    return false;
}

// Removing everything through the cart UI is a deliberate empty - never undo it.
function watchGuestCartEdits() {
    document.addEventListener('cart:item-remove', (event) => {
        const cart = event && event.detail && event.detail.cart;
        if (!cart || !cart.item_count) disarmSafetyNet('removed-by-shopper');
    });
}

// Login merge check. mergeNewLinesIntoAccountCart() below is unchanged; this
// only records what it was asked to merge and, on the next signed-in load,
// adds any of those products it failed to add (e.g. its request hit a 429).
// Same rule as the merge: only products the account cart does not hold at
// all, so quantities the account already has are never touched.
const LOGIN_MERGE_KEY = 'bm_login_pending_merge';
const LOGIN_MERGE_MAX_ATTEMPTS = 3;

function readLoginMerge() {
    try {
        const parsed = JSON.parse(localStorage.getItem(LOGIN_MERGE_KEY) || 'null');
        return parsed && Array.isArray(parsed.lines) && parsed.lines.length ? parsed : null;
    } catch (e) {
        return null;
    }
}

function writeLoginMerge(lines, attempts) {
    try {
        if (lines && lines.length) {
            localStorage.setItem(LOGIN_MERGE_KEY, JSON.stringify({lines, attempts: attempts || 0}));
        } else {
            localStorage.removeItem(LOGIN_MERGE_KEY);
        }
    } catch (e) { /* private mode */ }
}

// Returns true when it changed the cart and the page is reloading.
async function completeLoginMerge(cart) {
    const pending = readLoginMerge();
    if (!pending) return false;
    if (pending.attempts >= LOGIN_MERGE_MAX_ATTEMPTS) {
        writeLoginMerge(null);
        logCartSync('merge-giveup', {lines: pending.lines.length});
        return false;
    }
    const present = new Set(((cart && cart.items) || []).map((item) => String(item.variant_id)));
    const missing = pending.lines.filter((line) => !present.has(String(line.id)));
    if (!missing.length) {
        writeLoginMerge(null);
        return false;
    }
    writeLoginMerge(pending.lines, pending.attempts + 1);
    logCartSync('merge-retry', {missing: missing.length, attempts: pending.attempts + 1});
    await addLines(missing);
    window.location.reload();
    return true;
}

function readCurrentCart() {
    return (window.CartStore && typeof window.CartStore.get === 'function')
        ? window.CartStore.get()
        : fetch('/cart.json').then((response) => response.json());
}

// The selected store lives in localStorage + cookies, i.e. per device, so
// unlike cart lines and `attributes['delivery-method']` it does NOT travel
// with the cart. We carry it alongside the token so an adopting device ends
// up on the same store as the one that saved the cart.
function readAdoptionGuard() {
    try {
        return sessionStorage.getItem(CART_SWAP_GUARD_KEY) || '';
    } catch (e) {
        return '';
    }
}

function writeAdoptionGuard(token) {
    try {
        sessionStorage.setItem(CART_SWAP_GUARD_KEY, token);
    } catch (e) { /* private mode */ }
}

function clearAdoptionGuard() {
    try {
        sessionStorage.removeItem(CART_SWAP_GUARD_KEY);
    } catch (e) { /* private mode */ }
}

function readSelectedStore() {
    try {
        return {
            id: localStorage.getItem('storeID') || '',
            oid: localStorage.getItem('storeOID') || '',
            name: localStorage.getItem('storeName') || '',
        };
    } catch (e) {
        return {id: '', oid: '', name: ''};
    }
}

function applySelectedStore(store) {
    if (!store || (!store.id && !store.oid)) return;
    const maxAge = 60 * 60 * 24 * CART_COOKIE_DAYS;
    document.cookie = `storeID=${encodeURIComponent(store.oid || '')}; path=/; domain=${CART_ROOT_DOMAIN}; SameSite=None; Secure; max-age=${maxAge}`;
    document.cookie = `storeName=${encodeURIComponent(store.name || '')}; path=/; domain=${CART_ROOT_DOMAIN}; SameSite=None; Secure; max-age=${maxAge}`;
    try {
        localStorage.setItem('storeID', String(store.id || ''));
        localStorage.setItem('storeOID', String(store.oid || store.id || ''));
        localStorage.setItem('storeName', String(store.name || ''));
    } catch (e) { /* private mode */ }
}

function sameStore(a, b) {
    return (a && a.oid) === (b && b.oid) && (a && a.id) === (b && b.id);
}

// Returns the cart token stored against the customer, or '' when none.
// Never throws: a failed lookup must not break the page, it just means we
// fall back to whatever cart this device already has.
async function fetchStoredCart(customerId) {
    try {
        const response = await fetch('/apps/apis/api/product/get_customer_cart_metafield', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({customerId}),
            keepalive: true,
        });
        if (!response.ok) return '';
        const result = await response.json();
        // Verified against the live proxy: the read endpoint surfaces
        // persistentCart.items as `result`, so the token comes back there.
        // Legacy records hold an item array or the "nullcart" sentinel - both
        // mean "no token", so only a plain non-sentinel string counts.
        const value = result && result.result;
        if (typeof value !== 'string' || !value || value === NO_CART_SENTINEL) return null;
        try {
            const parsed = JSON.parse(value);
            if (parsed && typeof parsed.token === 'string' && parsed.token) return parsed;
        } catch (e) { /* legacy record */ }
        return null;
    } catch (e) {
        return '';
    }
}

// Pass '' as the token to clear the pointer (cart emptied or order placed).
async function storeCartToken(customerId, token) {
    try {
        await fetch('/apps/apis/api/product/update_customer_cart_metafield', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                customerId,
                // The proxy only reads back persistentCart.items, so the token
                // has to travel in `items`. Keeping the existing "nullcart"
                // sentinel for "no cart" so old and new records read alike.
                persistentCart: {
                    items: token
                        ? JSON.stringify({token, store: readSelectedStore()})
                        : NO_CART_SENTINEL,
                },
            }),
            keepalive: true,
        });
    } catch (e) {
        // Losing a pointer write is recoverable - the next cart action retries.
        console.error('Persistent cart: failed to store cart token', e);
    }
}

/**
 * Fold products the shopper added while signed out into the account's cart.
 *
 * The account cart is authoritative for everything it already holds, so a
 * quantity changed while signed out reverts, and a product removed while
 * signed out comes back. Only variants the account does not have at all are
 * carried over - additions propagate, edits and deletions do not.
 *
 * Must run after the cookie has been switched to the account cart, so that
 * /cart.js and /cart/add.js both address that cart.
 */
function getAutoAddedVariantIds() {
    const t = window.ShopifyTranslations || {};
    return new Set(
        [t.palletProductID, t.surchargeProductID]
            .map((gid) => String(gid || '').replace('gid://shopify/ProductVariant/', '').trim())
            .filter(Boolean),
    );
}

async function mergeNewLinesIntoAccountCart(guestItems) {
    // Pallet deposit and fuel surcharge lines are added by the storefront
    // itself from the delivery method and store, and are recalculated for
    // whichever cart is active. Carrying them over would plant a stale
    // surcharge in the account's cart that nothing asked for - the same reason
    // the reorder flow excludes them.
    const autoAdded = getAutoAddedVariantIds();
    const incoming = (Array.isArray(guestItems) ? guestItems : []).filter(
        (item) => !autoAdded.has(String(item && item.variant_id)),
    );
    if (!incoming.length) return;

    try {
        const response = await fetch('/cart.js', {headers: {Accept: 'application/json'}});
        if (!response.ok) return;
        const accountCart = await response.json();
        const alreadyThere = new Set(
            (accountCart.items || []).map((item) => String(item.variant_id)),
        );

        const additions = incoming
            .filter((item) => !alreadyThere.has(String(item.variant_id)))
            .map((item) => ({
                id: item.variant_id,
                quantity: item.quantity,
                properties: item.properties || {},
            }));

        if (!additions.length) return;

        await fetch('/cart/add.js', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({items: additions}),
        });
    } catch (e) {
        console.error('Persistent cart: failed to merge signed-out additions', e);
    }
}


/**
 * Keeps the `cart` cookie in step with the real cart, and - for a logged-in
 * customer - with the cart token stored against their account.
 *
 * Resolution rule: on load the stored pointer wins, so a device that is on a
 * different cart adopts the account's cart. Once both devices share a token
 * they mutate the same Shopify cart, so they stop diverging and the pointer
 * only moves again when a genuinely new cart is created.
 */
async function resetCookies() {
    let cart;
    try {
        cart = await readCurrentCart();
    } catch (e) {
        console.error('Cart sync error:', e);
        return;
    }

    const customerId = getPersistentCartCustomerId();
    const isCheckedOut = window.location.pathname.includes('/thank_you');
    const isEmpty = !cart || cart.item_count === 0;

    // Signed-in customer changed since the last load. Write the new identity
    // first so the reload below cannot detach a second time.
    const lastCustomerId = readLastCustomerId();
    logCartSync('load', {
        customer: customerId ? 'signed-in' : 'guest',
        changed: lastCustomerId !== customerId,
        token: shortToken(cart && cart.token),
        items: (cart && cart.item_count) || 0,
    });
    let justSignedIn = false;
    if (lastCustomerId !== customerId) {
        writeLastCustomerId(customerId);
        clearAdoptionGuard();
        if (lastCustomerId) {
            await detachFromAccountCart(cart, lastCustomerId);
            return;
        }
        // Guest -> signed in: anything added while signed out should follow.
        justSignedIn = Boolean(customerId);
    }

    // Belt and braces: if we are not signed in but are still holding a cart
    // this device last used while signed in, we were signed out without that
    // transition ever being observed here - the shopper may have used only the
    // account app, where this script does not load. Detach anyway, or every
    // edit made now lands on the account's cart and reaches their other
    // devices.
    if (!customerId && cart && cart.token && readAccountToken() === cart.token) {
        writeAccountToken('');
        await detachFromAccountCart(cart);
        return;
    }

    // Order placed: drop the cookie and the pointer so the next visit does
    // not resume a spent cart.
    if (isCheckedOut) {
        clearCartCookie();
        clearAdoptionGuard();
        writeAccountToken('');
        writeAccountSnapshot('', null);
        disarmSafetyNet('order-placed');
        writeLoginMerge(null);
        if (customerId) await storeCartToken(customerId, '');
        return;
    }

    // Signed back in: the account cart is authoritative again and guest
    // additions are merged below, so an unfinished copy no longer applies.
    if (customerId) {
        writePendingCopy(null);
        disarmSafetyNet('signed-in');
    } else {
        if (await completePendingCopy(cart)) return;
        if (await checkSafetyNet(cart, isEmpty)) return;
    }

    // Guests have no account to sync against - original cookie behaviour.
    if (!customerId) {
        if (isEmpty) clearCartCookie();
        else if (cart.token) writeCartCookieToken(cart.token);
        return;
    }

    const stored = await fetchStoredCart(customerId);
    const storedToken = stored ? stored.token : '';

    // Adopt the account's cart when this device is on a different one.
    // This has to run even when THIS device's cart is empty: a second device
    // that has not added anything yet is precisely the case to cover.
    if (storedToken && storedToken !== cart.token) {
        if (readAdoptionGuard() === storedToken) {
            // Already swapped to this token and still not on it, so the saved
            // cart is gone. Replace the pointer with whatever we hold.
            logCartSync('adopt-giveup', {stored: shortToken(storedToken), items: cart.item_count || 0});
            // Extra check: keep signed-out products the merge did not land.
            if (await completeLoginMerge(cart)) return;
            await storeCartToken(customerId, isEmpty ? '' : cart.token);
            if (isEmpty) clearCartCookie();
            // Extra check: this cart is the account's now. Record it the way the
            // next signed-in load would, so a logout before that load still has
            // a copy to restore from.
            else {
                writeAccountToken(cart.token);
                writeAccountSnapshot(customerId, cart);
            }
            return;
        }
        writeAdoptionGuard(storedToken);
        // Bring the saved store across, otherwise a Store-Pickup cart can
        // land on a device pointing at a different branch.
        applySelectedStore(stored.store);

        // Capture the signed-out cart before switching away from it.
        const signedOutItems = justSignedIn ? (cart.items || []) : [];
        // Extra check: remember what the merge is asked to add (see completeLoginMerge).
        if (justSignedIn) writeLoginMerge(toCopyLines(signedOutItems));
        logCartSync('adopt', {stored: shortToken(storedToken), merging: signedOutItems.length});
        writeAccountToken(storedToken);
        writeCartCookieToken(storedToken);
        await mergeNewLinesIntoAccountCart(signedOutItems);
        window.location.reload();
        return;
    }

    // Extra check: products the login merge was asked to add are really there.
    if (await completeLoginMerge(cart)) return;

    // On the account's cart, or the account has none saved yet.
    if (!isEmpty && cart.token) {
        writeAccountToken(cart.token);
        writeAccountSnapshot(customerId, cart);
        writeCartCookieToken(cart.token);
        // Publish when there is no pointer, or when only the store moved
        // (changing branch does not create a new cart).
        if (!storedToken || !sameStore(stored && stored.store, readSelectedStore())) {
            await storeCartToken(customerId, cart.token);
        }
        return;
    }

    // Empty cart. If the pointer matches, the shared cart is simply empty and
    // both devices should agree on that - leave it alone. Only tidy up when
    // the account has no cart saved at all.
    writeAccountSnapshot('', null);
    if (!storedToken) clearCartCookie();
}

if (getPersistentCartCustomerId()) {
    watchLogoutLinks(getPersistentCartCustomerId());
    watchAccountCart(getPersistentCartCustomerId());
} else {
    watchGuestCartEdits();
}
resetCookies();
